import React from 'react';
import { Target, CheckCircle2, Clock, XCircle } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { computeBalanceSheet } from '../../utils/balanceSheet';

/**
 * ObjectivesWidget — compact card listing the apprentice's currently active
 * pedagogical objectives (set by their Responsable Pédagogique).
 * Computes a live current value for each objective's metric from gameStore.
 * If no objectives are tracked, returns null (silent).
 */

const fmtNumber = (n, metric) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const isMoney = ['treasury', 'equity', 'monthly_revenue', 'monthly_expenses'].includes(metric);
  if (isMoney) return Math.round(n).toLocaleString('fr-FR') + ' €';
  if (metric === 'drivers_avg_satisfaction') return Math.round(n) + ' %';
  return Math.round(n).toLocaleString('fr-FR');
};

function readMetric(state, metric) {
  const drivers = state.drivers || [];
  const vehicles = state.vehicles || [];
  switch (metric) {
    case 'treasury':                 return state.treasury || 0;
    case 'equity':                   return (computeBalanceSheet(state).equity || 0);
    case 'monthly_revenue':          return state.monthlyRevenues || 0;
    case 'monthly_expenses':         return state.monthlyExpenses || 0;
    case 'fleet_total':              return vehicles.length;
    case 'fleet_active':             return vehicles.filter(v => v.status === 'active').length;
    case 'drivers_total':            return drivers.length;
    case 'drivers_avg_satisfaction': return drivers.length === 0 ? 0 : Math.round(drivers.reduce((a, d) => a + (d.satisfaction || 0), 0) / drivers.length);
    case 'reputation':               return Math.round(state.reputation || 0);
    case 'notoriety':                return Math.round(state.notoriety || 0);
    case 'contracts_active':         return (state.activeContracts || []).length;
    case 'depot_phase':              return state.depotPhase || 0;
    case 'total_days':               return state.totalDays || 0;
    default:                         return null;
  }
}

function progressPct(current, threshold, operator) {
  if (current == null || threshold == null) return 0;
  const c = Number(current);
  const t = Number(threshold);
  if (Number.isNaN(c) || Number.isNaN(t) || t === 0) return 0;
  if (operator === '>=') {
    return Math.max(0, Math.min(100, Math.round((c / t) * 100)));
  }
  // For '<=' operator, we're closer to goal when current is lower.
  if (c <= t) return 100;
  return Math.max(0, Math.min(100, Math.round((t / c) * 100)));
}

export default function ObjectivesWidget() {
  const objectives = useGameStore(s => s.pedagogicalObjectives) || [];
  const state = useGameStore();

  // 10/02/2026 (Lot J1) — Retour user prod : « L'objectif pédagogique
  // une fois atteint doit disparaître. » Le backend ack-er l'objectif
  // au prochain polling (5 min ou changement de mois), mais on filtre
  // déjà côté UI pour une disparition immédiate dès que pct >= 100 %.
  const visibleObjectives = (objectives || []).filter(obj => {
    const current = readMetric(state, obj.metric);
    const pct = progressPct(current, obj.threshold, obj.operator);
    return pct < 100;
  });

  if (!visibleObjectives || visibleObjectives.length === 0) return null;

  return (
    <div className="surface-card overflow-hidden" data-testid="objectives-widget">
      <div className="px-4 py-3 border-b border-ink-600 flex items-center justify-between bg-purple-500/5">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-purple-300" />
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
            Objectifs pédagogiques
          </h3>
        </div>
        <span className="text-[10px] text-slate-500 font-mono">{visibleObjectives.length} actif·s</span>
      </div>
      <div className="p-4 space-y-3">
        {visibleObjectives.map(obj => {
          const current = readMetric(state, obj.metric);
          const pct = progressPct(current, obj.threshold, obj.operator);
          const completed = pct >= 100;
          const daysLeft = obj.deadline_day != null ? (obj.deadline_day - (state.totalDays || 0)) : null;
          const overdue = daysLeft != null && daysLeft < 0;
          return (
            <div
              key={obj.id}
              className="rounded-md border border-ink-600 bg-ink-700/30 p-3"
              data-testid={`objective-${obj.id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    {completed ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-money flex-shrink-0" />
                    ) : overdue ? (
                      <XCircle className="w-3.5 h-3.5 text-danger flex-shrink-0" />
                    ) : (
                      <Clock className="w-3.5 h-3.5 text-purple-300 flex-shrink-0" />
                    )}
                    <p className="text-sm font-semibold text-slate-100 truncate">{obj.title}</p>
                  </div>
                  <p className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">
                    {obj.metric_label || obj.metric} {obj.operator} {fmtNumber(obj.threshold, obj.metric)}
                    {obj.deadline_day != null && (
                      <span className={`ml-2 ${overdue ? 'text-danger' : 'text-slate-500'}`}>
                        · {overdue ? 'délai dépassé' : `J${obj.deadline_day} (${daysLeft}j restants)`}
                      </span>
                    )}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-sm font-bold font-mono ${completed ? 'text-money' : 'text-slate-100'}`}>
                    {fmtNumber(current, obj.metric)}
                  </p>
                  <p className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">actuel</p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="h-1.5 bg-ink-800 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    completed ? 'bg-money' : overdue ? 'bg-danger' : 'bg-purple-400'
                  }`}
                  style={{ width: `${pct}%` }}
                  data-testid={`objective-progress-${obj.id}`}
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-1 font-mono">
                {pct}% — fixé par {obj.master_holder || 'votre Responsable'}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
